import React, { Component } from 'react';
import '!style-loader!css-loader!./SidebarDeviceList.css';

// サイドパネル用: デバイス一覧の個別行
const SidebarDeviceRow = ({ device, isTracking, onTrackDevice, organizationId, showEditLink = true }) => {
    // 最終受信の3状態判定
    // API経由: lastGPS/lastAccess/fix（10秒ポーリング）
    // WebSocket経由: emergency_reason（リアルタイム、NOSIGNALでオフライン検出）
    // 1. GPS受信中: ONLINE かつ fix=1 かつ NOSIGNAL以外 → 緑
    // 2. 通信のみ: ONLINE かつ fix=0 かつ NOSIGNAL以外 → グレー
    // 3. オフライン: statusなし or NOSIGNAL受信 → 赤
    let lastStatusClass = 'sidebar-dl-last-offline'; // デフォルト: オフライン（赤）
    let lastText = '-';

    const isOnline = device.status === 'ONLINE';
    // WebSocket経由のNOSIGNALチェック（リアルタイムでオフライン検出）
    const isNosignal = device.emergency_reason && device.emergency_reason.includes('NOSIGNAL');

    if (isOnline && !isNosignal && device.fix === 1) {
        // GPS受信あり
        lastStatusClass = 'sidebar-dl-last-gps';
        lastText = device.lastGPS || device.time || '-';
    } else if (isOnline && !isNosignal && device.fix === 0) {
        // 通信のみ（GPSなし）
        lastStatusClass = 'sidebar-dl-last-comm';
        lastText = device.lastAccess || device.time || '通信中';
    } else {
        // オフライン（APIで未検出 or NOSIGNAL受信）
        lastStatusClass = 'sidebar-dl-last-offline';
        lastText = 'OFFLINE';
    }

    // センサー編集ページのURL
    const sensorEditUrl = device.sensorId
        ? `/sensors/${device.sensorId}/edit`
        : null;

    return (
        <div className={`sidebar-dl-row ${isTracking ? 'sidebar-dl-row-tracking' : ''}`}>
            <span className="sidebar-dl-col-serial">{device.deviceId}</span>
            <span className="sidebar-dl-col-name">
                {device.disp_name || device.name}
                {showEditLink && sensorEditUrl && (
                    <a href={sensorEditUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sidebar-dl-edit-link"
                        title="センサー編集"
                        onClick={(e) => e.stopPropagation()}>
                        ✎
                    </a>
                )}
            </span>
            <span className={`sidebar-dl-col-last ${lastStatusClass}`}>{lastText}</span>
            <span className="sidebar-dl-col-track">
                <input
                    type="checkbox"
                    className="sidebar-dl-checkbox"
                    checked={isTracking}
                    onChange={() => onTrackDevice && onTrackDevice(device.deviceId)}
                />
            </span>
        </div>
    );
};

// デバイスのステータスを判定するヘルパー関数
// 返り値: 'online_gps' | 'online_comm' | 'offline'
const getDeviceStatus = (device) => {
    const isOnline = device.status === 'ONLINE';
    const isNosignal = device.emergency_reason && device.emergency_reason.includes('NOSIGNAL');
    if (isOnline && !isNosignal && device.fix === 1) return 'online_gps';
    if (isOnline && !isNosignal && device.fix === 0) return 'online_comm';
    return 'offline';
};

// ステータスサマリーバー
const SidebarStatusSummary = ({ deviceMap, activeFilter, onFilter }) => {
    // 各カウントを計算
    let onlineCount = 0;
    let offlineCount = 0;
    let gpsCount = 0;
    let noGpsCount = 0;

    deviceMap.forEach(d => {
        const status = getDeviceStatus(d);
        if (status === 'online_gps') {
            onlineCount++;
            gpsCount++;
        } else if (status === 'online_comm') {
            onlineCount++;
            noGpsCount++;
        } else {
            offlineCount++;
        }
    });

    // バッジのアクティブ状態クラス
    const badgeClass = (filter) => {
        const isActive = activeFilter === filter;
        return `sidebar-dl-badge ${isActive ? 'sidebar-dl-badge-active' : ''}`;
    };

    return (
        <div className="sidebar-dl-summary">
            <span className={`${badgeClass('online')} sidebar-dl-badge-online`}
                onClick={() => onFilter('online')}>
                Online <span className="sidebar-dl-badge-count">{onlineCount}</span>
            </span>
            <span className={`${badgeClass('offline')} sidebar-dl-badge-offline`}
                onClick={() => onFilter('offline')}>
                Offline <span className="sidebar-dl-badge-count">{offlineCount}</span>
            </span>
            <span className={`${badgeClass('gps')} sidebar-dl-badge-gps`}
                onClick={() => onFilter('gps')}>
                GPS受信 <span className="sidebar-dl-badge-count">{gpsCount}</span>
            </span>
            <span className={`${badgeClass('nogps')} sidebar-dl-badge-nogps`}
                onClick={() => onFilter('nogps')}>
                GPS未受信 <span className="sidebar-dl-badge-count">{noGpsCount}</span>
            </span>
            {activeFilter && (
                <span className="sidebar-dl-badge sidebar-dl-badge-reset"
                    onClick={() => onFilter(activeFilter)}>
                    ✕ 解除
                </span>
            )}
        </div>
    );
};

export default class SidebarDeviceList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '',
            statusFilter: null, // null=全て, 'online', 'offline', 'gps', 'nogps'
        };
        this.handleSearch = this.handleSearch.bind(this);
        this.handleStatusFilter = this.handleStatusFilter.bind(this);
    }

    handleSearch(e) {
        this.setState({ search: e.target.value });
    }

    // バッジクリックでフィルター切り替え（同じバッジ再クリックで解除）
    handleStatusFilter(filter) {
        this.setState(prev => ({
            statusFilter: prev.statusFilter === filter ? null : filter
        }));
    }

    render() {
        const { deviceMap, trackingDeviceId, onTrackDevice } = this.props;
        if (!deviceMap || deviceMap.length === 0) return null;

        // 検索フィルタ
        const q = this.state.search.toLowerCase();
        let filtered = q
            ? deviceMap.filter(d => {
                const name = (d.disp_name || d.name || '').toLowerCase();
                const id = (d.deviceId || '').toString().toLowerCase();
                return name.includes(q) || id.includes(q);
            })
            : deviceMap;

        // ステータスフィルタ
        const sf = this.state.statusFilter;
        if (sf) {
            filtered = filtered.filter(d => {
                const status = getDeviceStatus(d);
                switch (sf) {
                    case 'online': return status === 'online_gps' || status === 'online_comm';
                    case 'offline': return status === 'offline';
                    case 'gps': return status === 'online_gps';
                    case 'nogps': return status === 'online_comm';
                    default: return true;
                }
            });
        }

        return (
            <div className="sidebar-dl">
                <div className="sidebar-dl-header">
                    <input
                        type="text"
                        className="sidebar-dl-search"
                        placeholder="デバイス検索..."
                        value={this.state.search}
                        onChange={this.handleSearch}
                    />
                </div>
                <SidebarStatusSummary
                    deviceMap={deviceMap}
                    activeFilter={this.state.statusFilter}
                    onFilter={this.handleStatusFilter}
                />
                <div className="sidebar-dl-columns">
                    <span className="sidebar-dl-col-serial">シリアル</span>
                    <span className="sidebar-dl-col-name">表示名</span>
                    <span className="sidebar-dl-col-last">最終受信</span>
                    <span className="sidebar-dl-col-track">追跡</span>
                </div>
                <div className="sidebar-dl-body">
                    {filtered.map(d => (
                        <SidebarDeviceRow
                            key={d.id}
                            device={d}
                            isTracking={trackingDeviceId === d.deviceId}
                            onTrackDevice={onTrackDevice}
                            organizationId={this.props.organizationId}
                            showEditLink={this.props.showEditLink !== false}
                        />
                    ))}
                    {filtered.length === 0 && (
                        <div className="sidebar-dl-empty">該当するデバイスがありません</div>
                    )}
                </div>
                <div className="sidebar-dl-footer">
                    {filtered.length} / {deviceMap.length} 件
                </div>
            </div>
        );
    }
}

